import { useState, useRef, useEffect } from 'react';
import { useForm, FieldValues } from 'react-hook-form';
import { message } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { queryClient } from 'config/query-client';
import { Link, useNavigate } from 'react-router-dom';

import {
  ACCOUNT_INFO,
  BUSINESS_TYPES,
  COUNTRIES,
  CURRENCIES,
  TIME_ZONES,
} from 'events/types';
import { useAuth } from 'providers/user-context';
import {
  getBusinessTypes,
  getCountries,
  getCurrencies,
  getLegalDocTypes,
} from 'services/collections';
import { uploadFile } from 'services/io';
import {
  createAccount,
  getAccountData,
  updateAccountInfo,
} from 'services/accounts';
import { getTimezones } from 'services/others';
import { CustomError } from 'types';

import useTranslation from '../../customHooks/translate';
import { Input, Select } from './styled';

const Details = () => {
  const [defaultValues, setDefaultValues] = useState();
  const [accountNameDisabled, setAccountNameDisabled] = useState(false);
  const [businessType, setBusinessType] = useState();
  const [formData, setFormData] = useState<any>(null);
  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>(
    {}
  );

  const translation = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const form = useRef(null);
  const signatureFileInput = useRef<HTMLInputElement>(null);
  const profilePhotoInput = useRef<HTMLInputElement>(null);

  const { handleSubmit, watch, setValue } = useForm<FieldValues>({
    mode: 'onBlur',
    values: defaultValues,
    shouldFocusError: true,
  });

  const businessTypesQuery = useQuery({
    queryKey: [BUSINESS_TYPES],
    queryFn: getBusinessTypes,
    select: (response) =>
      response.data.data.map((type) => ({
        value: type.businessTypeId,
        label: type.businessTypeName,
      })),
  });

  const countriesQuery = useQuery({
    queryKey: [COUNTRIES],
    queryFn: getCountries,
    select: (response) =>
      response.data.data.map((c) => ({ value: c.id, label: c.name })),
  });

  const accountInfoQuery = useQuery({
    queryKey: [ACCOUNT_INFO],
    queryFn: getAccountData,
    select: (response) => response.data.data,
  });

  const timeZonesQuery = useQuery({
    queryKey: [TIME_ZONES],
    queryFn: getTimezones,
    select: (response) =>
      response.data.data.map((d) => ({
        value: d.timeZoneId,
        label: d.timeZoneName,
      })),
  });

  const currenciesQuery = useQuery({
    queryKey: [CURRENCIES],
    queryFn: getCurrencies,
    select: (response) =>
      response.data.data.map((d) => ({ value: d.currencyId, label: d.name })),
  });

  useEffect(() => {
    if (
      [
        accountInfoQuery,
        currenciesQuery,
        countriesQuery,
        businessTypesQuery,
        timeZonesQuery,
      ].every((q) => q.isSuccess) &&
      !formData
    ) {
      const currency = accountInfoQuery.data!.defaultCurrencyId;
      const currencyOptions = currenciesQuery.data!.find(
        (currencyOption) => currencyOption.value === currency
      );

      const businessType = accountInfoQuery.data!.businessTypeId;
      const businessTypeOptions = businessTypesQuery.data!.find(
        (businessTypeOption) => businessTypeOption.value === businessType
      );

      const country = accountInfoQuery.data!.countryId;
      const countryId = countriesQuery.data!.find(
        (countryQuery) => countryQuery.value === country
      );

      const timeZone = accountInfoQuery.data!.timeZoneId;
      const timeZoneId = timeZonesQuery.data!.find(
        (timeZoneQuery) => timeZoneQuery.value === timeZone
      );

      const formData = JSON.parse(JSON.stringify(accountInfoQuery.data));
      formData.defaultCurrencyId = currencyOptions;
      formData.businessTypeId = businessTypeOptions;
      formData.countryId = countryId;
      formData.timeZoneId = timeZoneId;

      setAccountNameStatus(businessType);
      setBusinessType(businessType);
      setFormData(formData);
    }
  }, [
    accountInfoQuery,
    currenciesQuery,
    countriesQuery,
    businessTypesQuery,
    timeZonesQuery,
    formData,
    setValue,
  ]);

  const handleFormSubmit = (data: FieldValues) => {
    const requestBody = {
      ...data,
      businessTypeId: data.businessTypeId
        ? parseInt(data.businessTypeId.value)
        : null,
      legalDocTypeId: data.legalDocTypeId
        ? parseInt(data.legalDocTypeId.value)
        : null,
      countryId: data.countryId ? parseInt(data.countryId.value) : null,
      defaultCurrencyId: data.defaultCurrencyId
        ? data.defaultCurrencyId.value
        : null,
      timeZoneId: data.timeZoneId ? data.timeZoneId.value : null,
    };

    const method =
      user.data.hasAccount === false ? createAccount : updateAccountInfo;

    method(requestBody)
      .then(() => {
        message.success(translation.messages.successUpdated);
        queryClient.invalidateQueries({ queryKey: ['accountInfo'] });
        navigate('/dashboard');
      })
      .catch((error: CustomError) => {
        setTimeout(() => {
          const firstErrorElement = document.querySelector(
            '[style*="border-color: red"], [style*="borderColor: red"]'
          );
          if (firstErrorElement) {
            firstErrorElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }, 100);
        error.response?.data?.Message &&
          message.error(error.response?.data?.Message);
        const extractedErrors = error.response?.data?.errors || {};
        setErrors({
          BusinessTypeId: extractedErrors.BusinessTypeId?.[0],
          AccountName: extractedErrors.AccountName?.[0],
          City: extractedErrors.City?.[0],
          CountryId: extractedErrors.CountryId?.[0],
          DefaultCurrencyId: extractedErrors.DefaultCurrencyId?.[0],
          LegalDocNumber: extractedErrors.LegalDocNumber?.[0],
          MobileNumber: extractedErrors.MobileNumber?.[0],
          Street: extractedErrors.Street?.[0],
          TimeZoneId: extractedErrors.TimeZoneId?.[0],
          Zip: extractedErrors.Zip?.[0],
          Fullname: extractedErrors.Fullname?.[0],
        });
      });
  };

  useEffect(() => {
    if (formData) {
      if (businessType) {
        getLegalDocTypes(businessType).then((legalDocType) => {
          const legalDocTypeId = {
            value: legalDocType.data.data[0].legalDocTypeId,
            label: legalDocType.data.data[0].legalDocTypeName,
          };
          setDefaultValues({
            ...formData,
            legalDocTypeId,
          });
        });
      } else if (!user['hasAccount']) {
        setDefaultValues(formData);
      }
    }
  }, [businessType, formData, user]);

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (name === 'businessTypeId' && value.businessTypeId) {
        getLegalDocTypes(value.businessTypeId.value).then((legalDocType) => {
          const newLegalDocType = {
            value: legalDocType.data.data[0].legalDocTypeId,
            label: legalDocType.data.data[0].legalDocTypeName,
          };
          setValue('legalDocTypeId', newLegalDocType);
        });
        setAccountNameStatus(value.businessTypeId.value);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  function setAccountNameStatus(businessTypeId: number) {
    if (businessTypeId !== 1) {
      setAccountNameDisabled(true);
      setValue('accountName', 'xxxxx');
    } else {
      setAccountNameDisabled(false);
    }
  }

  const mapFieldName = (name: string) => {
    const fieldMapping: { [key: string]: string } = {
      businessTypeId: 'BusinessTypeId',
      accountName: 'AccountName',
      city: 'City',
      countryId: 'CountryId',
      defaultCurrencyId: 'DefaultCurrencyId',
      legalDocNumber: 'LegalDocNumber',
      mobileNumber: 'MobileNumber',
      street: 'Street',
      timeZoneId: 'TimeZoneId',
      zip: 'Zip',
      fullName: 'Fullname',
    };
    return fieldMapping[name] || name;
  };

  const handleSelectChange = (name: string, option: any) => {
    const mappedName = mapFieldName(name);
    setFormData((prevData) => ({
      ...prevData,
      [name]: option,
    }));

    if (name === 'businessTypeId' && option) {
      getLegalDocTypes(option.value).then((legalDocType) => {
        const newLegalDocType = {
          value: legalDocType.data.data[0].legalDocTypeId,
          label: legalDocType.data.data[0].legalDocTypeName,
        };
        setValue('legalDocTypeId', newLegalDocType);
        setFormData((prevData) => ({
          ...prevData,
          legalDocTypeId: newLegalDocType,
        }));
      });
    }

    if (errors[mappedName]) {
      setErrors((prevErrors) => {
        const { [mappedName]: _, ...remainingErrors } = prevErrors;
        return remainingErrors;
      });
    }
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (name === 'accountName' && errors.AccountName) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        AccountName: undefined,
      }));
    }
    if (name === 'fullName' && errors.Fullname) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        Fullname: undefined,
      }));
    }
    if (name === 'email' && errors.Email) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        Email: undefined,
      }));
    }
    if (name === 'city' && errors.City) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        City: undefined,
      }));
    }

    if (name === 'street' && errors.Street) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        Street: undefined,
      }));
    }
    if (name === 'mobileNumber' && errors.MobileNumber) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        MobileNumber: undefined,
      }));
    }

    if (name === 'zip' && errors.Zip) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        Zip: undefined,
      }));
    }

    if (name === 'legalDocNumber' && errors.LegalDocNumber) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        LegalDocNumber: undefined,
      }));
    }
  };

  return (
    <div className="md:w-[80%] xl:w-[50%]">
      <div className="mb-[24px] flex flex-col gap-[12px]">
        <p className="text-[20px] font-medium text-[#1D1D1D] md:text-[24px]">
          {translation.info.personalInfo}
        </p>
        <p className="text-[14px] font-normal text-[#1D1D1D]">
          {translation.info.personalInfoText}
        </p>
      </div>
      <form onSubmit={handleSubmit(handleFormSubmit)} ref={form}>
        <div className="flex flex-col gap-[20px] md:gap-[32px]">
          <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[20px] md:gap-[12px]">
            <div className="flex flex-col gap-[10px]">
              <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                {translation.info.company_name}
              </label>
              <Input
                type="text"
                value={formData?.accountName}
                onChange={handleInputChange}
                maxLength={100}
                name="accountName"
                placeholder={translation.info.company_name}
                style={{
                  borderColor: errors.AccountName ? 'red' : '',
                }}
              />
              {errors.AccountName && (
                <div className="text-[12px] text-red-600">
                  {errors?.AccountName}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-[10px]">
              <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                {translation.info.name}
              </label>
              <Input
                type="text"
                value={formData?.fullName}
                onChange={handleInputChange}
                name="fullName"
                placeholder={translation.info.name}
                style={{
                  borderColor: errors.Fullname ? 'red' : '',
                }}
              />
              {errors.Fullname && (
                <div className="text-[12px] text-red-600">
                  {errors.Fullname}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-[10px]">
            <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
              {translation.info.email_address}
            </label>
            <Input
              type="text"
              value={formData?.email}
              onChange={handleInputChange}
              name="email"
              placeholder={translation.info.email_address}
              style={{
                borderColor: errors.Email ? 'red' : '',
              }}
            />
            {errors.Email && (
              <div className="text-[12px] text-red-600">{errors.Email}</div>
            )}
          </div>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[20px] md:gap-[12px]">
            <div className="flex flex-col gap-[10px]">
              <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                {translation.info.country}
              </label>
              <Select
                options={countriesQuery.data}
                classNamePrefix="react-select"
                onChange={(option: any) =>
                  handleSelectChange('countryId', option)
                }
                name="countryId"
                value={formData?.countryId}
                placeholder={translation.info.country}
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    borderColor: errors.CountryId
                      ? 'red'
                      : baseStyles.borderColor,
                  }),
                  menu: (baseStyles) => ({
                    ...baseStyles,
                    zIndex: 100,
                  }),
                }}
              />
              {errors.CountryId && (
                <div className="text-[12px] text-red-600">
                  {errors.CountryId}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-[10px]">
              <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
                {translation.info.city}
              </label>
              <Input
                type="text"
                value={formData?.city}
                onChange={handleInputChange}
                name="city"
                placeholder={translation.info.city}
                style={{
                  borderColor: errors.City ? 'red' : '',
                }}
              />
              {errors.City && (
                <div className="text-[12px] text-red-600">{errors.City}</div>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-[10px]">
            <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
              {translation.info.street}
            </label>
            <Input
              type="text"
              value={formData?.street}
              onChange={handleInputChange}
              name="street"
              maxLength={60}
              placeholder={translation.info.street}
              style={{
                borderColor: errors.Street ? 'red' : '',
              }}
            />
            {errors.Street && (
              <div className="text-[12px] text-red-600">{errors.Street}</div>
            )}
          </div>

          <div className="flex flex-col gap-[10px]">
            <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
              {translation.info.mobile_number}
            </label>
            <Input
              type="text"
              value={formData?.mobileNumber}
              onChange={handleInputChange}
              maxLength={13}
              name="mobileNumber"
              placeholder={translation.info.mobile_number}
              style={{
                borderColor: errors.MobileNumber ? 'red' : '',
              }}
            />
            {errors.MobileNumber && (
              <div className="text-[12px] text-red-600">
                {errors.MobileNumber}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-[10px] rounded-[10px] bg-gray-200 p-[20px]">
            <p className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
              {translation.info.profile_photo}
            </p>
            <input
              type="file"
              ref={profilePhotoInput}
              onChange={(event) => {
                const fileUploaded = event.target.files?.length
                  ? event.target.files[0]
                  : null;
                fileUploaded && uploadFile(fileUploaded, 3);
              }}
              style={{ display: 'none' }}
            />
            <div
              className="cursor-pointer"
              onClick={() => {
                profilePhotoInput.current!.click();
              }}
            >
              <p className="font-medium">{translation.info.click_to_upload}</p>
              <p>SVG, PNG, JPG, GIF (max. 800x400px)</p>
            </div>
          </div>

          <div className="flex flex-col gap-[10px]">
            <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
              {translation.info.zip}
            </label>
            <Input
              type="text"
              value={formData?.zip}
              onChange={handleInputChange}
              name="zip"
              placeholder={translation.info.zip}
              style={{
                borderColor: errors.Zip ? 'red' : '',
              }}
            />
            {errors.Zip && (
              <div className="text-[12px] text-red-600">{errors.Zip}</div>
            )}
          </div>

          <div className="flex flex-col gap-[10px]">
            <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
              {translation.info.tin}
            </label>
            <Input
              type="text"
              value={formData?.legalDocNumber}
              onChange={handleInputChange}
              name="legalDocNumber"
              placeholder={translation.info.tin}
              style={{
                borderColor: errors.LegalDocNumber ? 'red' : '',
              }}
            />
            {errors.LegalDocNumber && (
              <div className="text-[12px] text-red-600">
                {errors.LegalDocNumber}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-[10px]">
            <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
              {translation.info.default_currency}
            </label>
            <Select
              options={currenciesQuery.data}
              classNamePrefix="react-select"
              onChange={(option: any) =>
                handleSelectChange('defaultCurrencyId', option)
              }
              name="defaultCurrencyId"
              value={formData?.defaultCurrencyId}
              placeholder={translation.info.default_currency}
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor: errors.DefaultCurrencyId
                    ? 'red'
                    : baseStyles.borderColor,
                }),
                menu: (baseStyles) => ({
                  ...baseStyles,
                  zIndex: 100,
                }),
              }}
            />
            {errors.DefaultCurrencyId && (
              <div className="text-[12px] text-red-600">
                {errors.DefaultCurrencyId}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-[10px]">
            <label className="text-[14px] font-medium text-[#1D1D1D] md:text-[16px]">
              {translation.info.time_zone}
            </label>
            <Select
              options={timeZonesQuery.data}
              classNamePrefix="react-select"
              onChange={(option: any) =>
                handleSelectChange('timeZoneId', option)
              }
              name="timeZoneId"
              value={formData?.timeZoneId}
              placeholder={translation.info.time_zone}
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor: errors.TimeZoneId
                    ? 'red'
                    : baseStyles.borderColor,
                }),
                menu: (baseStyles) => ({
                  ...baseStyles,
                  zIndex: 100,
                }),
              }}
            />
            {errors.TimeZoneId && (
              <div className="text-[12px] text-red-600">
                {errors.TimeZoneId}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-[10px] rounded-[10px] bg-gray-200 p-[20px]">
            <p className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]">
              {translation.info.signature_stamp}
            </p>
            <input
              type="file"
              ref={signatureFileInput}
              onChange={(event) => {
                const fileUploaded = event.target.files?.length
                  ? event.target.files[0]
                  : null;
                fileUploaded && uploadFile(fileUploaded, 1);
              }}
              style={{ display: 'none' }}
            />
            <div
              className="cursor-pointer"
              onClick={() => {
                signatureFileInput.current!.click();
              }}
            >
              <p className="font-medium">{translation.info.click_to_upload}</p>
              <p>SVG, PNG, JPG, GIF (max. 800x400px)</p>
            </div>
          </div>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[10px]">
            <button className="w-full rounded-[6px] border border-[rgba(41,45,50,0.25)] bg-white px-[24px] py-[12px] text-[14px] font-normal leading-[16px] text-[#292d32] md:text-[16px]">
              <Link className="hover:text-inherit" to={'/dashboard'}>
                {translation.info.cancelButton}
              </Link>
            </button>
            <button
              onClick={handleSubmit(handleFormSubmit)}
              className="w-full rounded-[6px] bg-[#466afb] px-[24px] py-[12px] text-[14px] font-normal leading-[16px] text-white md:text-[16px]"
            >
              {translation.info.saveButton}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Details;
